"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DAY_MARKER = exports.DAY_SUFFIX = exports.DURATION_UNITS = void 0;
exports.hoursToDays = hoursToDays;
exports.daysToHours = daysToHours;
exports.durationValue = durationValue;
exports.formatDays = formatDays;
exports.formatDurationNumber = formatDurationNumber;
exports.formatDurationShort = formatDurationShort;
exports.formatDuration = formatDuration;
exports.formatDurationSigned = formatDurationSigned;
exports.parseDuration = parseDuration;
const economics_1 = require("./contracts/economics");
const hours_1 = require("./hours");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · a duration in the READER'S unit: hours (`H:MM`) or man-days. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's ruling (`decisions.md` DU-1): each person chooses whether they see and type
 * time as hours or as man-days, and EVERYTHING follows the choice — screens, charts, reports,
 * exports, the Agent's answers and the emails they receive. **The wire and the database stay in
 * hours**; this file is the one place a figure crosses into days and back.
 *
 * ⚠️ **A FIXED 8 h man-day** (`MAN_DAY_HOURS`, the same constant the day rate divides by). Not the
 * person's schedule: a man-day is a unit of effort, and a reader comparing two people's figures
 * must not see the same eight hours as `1` for one and `1,14` for the other.
 *
 * ⚠️ **Two decimals, trailing zeros trimmed, the locale's decimal mark** — `1,5 nap` / `1.5 d`,
 * `0,06 nap` for half an hour. The suffix makes a days figure self-describing the way the colon
 * makes `7:30` self-describing, so a string rendered anywhere — a chip, a tooltip, a mail — cannot
 * be misread as hours. Templates whose words already name the unit carry a `.days` sibling.
 */
exports.DURATION_UNITS = ['hours', 'days'];
/**
 * The man-day's abbreviation. ⚠️ Here rather than in a screen catalogue because the API writes it
 * too (mail, the Agent, exports) and two copies would drift — `permissions/labels.ts` is the
 * precedent for a shared string table.
 */
exports.DAY_SUFFIX = { hu: 'nap', en: 'd' };
/**
 * The man-day's marker in a NARROW cell — a chip, a calendar day, a grid box — where the figure
 * once went bare (4 Nov 2026, the product owner: "MWD" in both languages). It parses back.
 */
exports.DAY_MARKER = 'MWD';
/** Hours to man-days, at the two places a days figure is shown with. */
function hoursToDays(hours) {
    return Math.round((hours / economics_1.MAN_DAY_HOURS) * 100) / 100;
}
/** Man-days to hours, at the `0.01 h` the columns store. */
function daysToHours(days) {
    return Math.round(days * economics_1.MAN_DAY_HOURS * 100) / 100;
}
/** A figure in the duration unit, as a plain number — what an export cell or a chart axis holds. */
function durationValue(hours, unit) {
    return unit === 'days' ? hoursToDays(hours) : hours;
}
/** `1,5` / `1.5` — a number of days with no suffix, two places at most, zeros trimmed. */
function formatDays(days, locale) {
    if (!Number.isFinite(days))
        return '0';
    const rounded = Math.round(days * 100) / 100;
    /* `Object.is(-0)` and `-0.001` both round to a zero that must not print a sign. */
    const text = (rounded === 0 ? 0 : rounded).toFixed(2).replace(/\.?0+$/u, '');
    return locale === 'hu' ? text.replace('.', ',') : text;
}
function toNumber(hours) {
    const value = typeof hours === 'string' ? Number(hours) : hours;
    return Number.isFinite(value) ? value : 0;
}
/**
 * The bare figure: `7:30` in hours, `0,94` in days. For a cell under a header that already names
 * the unit, or a template that writes the unit word itself.
 */
function formatDurationNumber(hours, unit, locale) {
    const value = toNumber(hours);
    return unit === 'days' ? formatDays(hoursToDays(value), locale) : (0, hours_1.formatHours)(value);
}
/** `7:30` in hours; `0,94 MWD` in days — a narrow cell's figure, still self-describing. */
function formatDurationShort(hours, unit, locale) {
    if (unit === 'hours')
        return (0, hours_1.formatHours)(hours);
    return `${formatDurationNumber(hours, unit, locale)} ${exports.DAY_MARKER}`;
}
/**
 * ⚠️ **THE FORMATTER EVERY SURFACE CALLS.** `7:30` in hours; `0,94 nap` / `0.94 d` in days.
 */
function formatDuration(hours, unit, locale) {
    if (unit === 'hours')
        return (0, hours_1.formatHours)(hours);
    return `${formatDurationNumber(hours, unit, locale)} ${exports.DAY_SUFFIX[locale]}`;
}
/**
 * `+3:30` / `−1:15` / `+0,44 nap` — a figure whose SIGN is the point. The minus is U+2212, and a
 * near-zero value gets no sign (`effort-format.ts` explains both).
 */
function formatDurationSigned(hours, unit, locale) {
    const value = toNumber(hours);
    const sign = value > 0.004 ? '+' : value < -0.004 ? '−' : '';
    return `${sign}${formatDuration(Math.abs(value), unit, locale)}`;
}
/**
 * Parse a typed duration into HOURS, in the typist's unit, or `null` when it is not one.
 *
 * In hours mode this IS `parseHours`. In days mode a bare number is days — `1`, `1,5`, `0.25`,
 * `1d`, `1n`, `1 nap` — times eight, rounded to `0.01 h`. ⚠️ **An explicit `h`, `m` or `:` is
 * still hours** (`4h` stays four hours): a person who writes the unit has said which one they
 * mean, and overruling them would be the silent factor-of-eight error this function exists to
 * prevent. ⚠️ The mirror holds in hours mode (3 Nov 2026): `1,5 nap` / `1.5d` is a day and a half
 * whichever unit is chosen — what a grid cell, which has no unit picker beside it, can be told.
 */
function parseDuration(input, unit) {
    if (typeof input === 'number') {
        if (unit === 'hours')
            return (0, hours_1.parseHours)(input);
        return Number.isFinite(input) && input >= 0 ? daysToHours(input) : null;
    }
    const text = input.trim().toLowerCase();
    if (text === '')
        return null;
    /* The day suffix is optional in days mode and required in hours mode, where a bare number is hours. */
    const days = /^(\d{1,4})(?:[.,](\d{1,4}))?\s*(d|n|nap|day|days|mwd)?$/u.exec(text);
    if (unit === 'hours' && days?.[3] === undefined)
        return (0, hours_1.parseHours)(text);
    if (days !== null) {
        const whole = days[1] ?? '0';
        const fraction = days[2] ?? '';
        return daysToHours(Number(fraction === '' ? whole : `${whole}.${fraction}`));
    }
    /* `4h`, `4:00`, `90m`, `1h30` — the typist named hours. */
    if (/[hm:]/u.test(text))
        return (0, hours_1.parseHours)(text);
    return null;
}
//# sourceMappingURL=duration.js.map